import axios from 'axios';
import { Activity } from '../types/Activity';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface CreateActivityData {
  activityName: string;
  unitOfMeasure: string;
  activityNumber: string;
  scheduled2Rate: number | null;
  serviceAreaId: number | null;
  contractYear: number | null;
  classification: string;
  locationCode?: string;
  activityType?: string;
  point?: boolean;
  linear?: boolean;
  activityProfile?: string;
  threshold?: number | null;
}

/**
 * What a write to /api/activities may carry.
 *
 * Wider than CreateActivityData because the profile attributes and work instructions are
 * writable too, and neither is shaped like a column on Activity (steps go up as a list). The
 * server defines the real contract by allowlist; this type exists so the fields are at least
 * visible to TypeScript on the path they travel.
 */
export interface ActivityWritePayload extends Partial<CreateActivityData> {
  workInstructionPurpose?: string | null;
  workInstructionSteps?: string[];
  activityCostings?: Array<{
    id?: number;
    serviceAreaId: number;
    contractYear: number;
    scheduled2Rate: number | null;
  }>;
  [key: string]: unknown;
}

export interface ActivityQueryParams {
  classification?: string;
  serviceAreaId?: number;
  contractYear?: number;
}

export interface PaginatedActivityParams {
  classification?: string;
  profile?: string;
  search?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface PaginatedActivities {
  data: Activity[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

class ActivityService {
  // Get all activities (optionally filtered)
  async getAllActivities(filters?: ActivityQueryParams): Promise<Activity[]> {
    try {
      const response = await axios.get(`${API_URL}/activities`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get a paginated / filtered / sorted / searched page of activities (Activities admin list).
  // Separate from getAllActivities so existing array-returning consumers are unaffected.
  async getActivitiesPaginated(params: PaginatedActivityParams): Promise<PaginatedActivities> {
    try {
      const response = await axios.get(`${API_URL}/activities/paginated`, {
        params,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get distinct profiles (for the Profile filter dropdown)
  async getProfileValues(): Promise<string[]> {
    try {
      const response = await axios.get(`${API_URL}/activities/profile-values`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get activity by ID
  async getActivityById(id: number): Promise<Activity> {
    try {
      const response = await axios.get(`${API_URL}/activities/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Create new activity
  async createActivity(activityData: ActivityWritePayload): Promise<Activity> {
    try {
      const response = await axios.post(`${API_URL}/activities`, activityData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update activity
  async updateActivity(id: number, activityData: ActivityWritePayload): Promise<Activity> {
    try {
      const response = await axios.put(`${API_URL}/activities/${id}`, activityData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete activity
  async deleteActivity(id: number): Promise<void> {
    try {
      await axios.delete(`${API_URL}/activities/${id}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get activities by service area
  async getActivitiesByServiceArea(serviceAreaId: number): Promise<Activity[]> {
    try {
      const activities = await this.getAllActivities({ serviceAreaId });
      return activities;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get activities by classification
  async getActivitiesByClassification(classification: string): Promise<Activity[]> {
    try {
      const activities = await this.getAllActivities({ classification });
      return activities;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get activities by service area and classification
  async getActivitiesByServiceAreaAndClassification(
    serviceAreaId: number,
    classification: string
  ): Promise<Activity[]> {
    try {
      const activities = await this.getAllActivities({ serviceAreaId, classification });
      return activities;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get quantified activities by service area and contract year
  async getQuantifiedActivitiesByServiceAreaAndYear(
    serviceAreaId: number,
    contractYear: number
  ): Promise<Activity[]> {
    try {
      const response = await axios.get(`${API_URL}/activities/quantified`, {
        params: {
          serviceAreaId,
          contractYear
        }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get distinct activity profiles
  async getActivityProfiles(): Promise<string[]> {
    try {
      const response = await axios.get(`${API_URL}/activities/profiles`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Error handling helper
  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.error || error.message;
      return new Error(message);
    }
    return error instanceof Error ? error : new Error('An unknown error occurred');
  }
}

export const activityService = new ActivityService();
export default activityService;
